import { AutoAdjustBackwardsLimit } from "../../constants/autoAdjustBackwardsLimit";
import { AutoAdjustDateLevel } from "../../constants/autoAdjustDateLevel";
import compactDisplays from "../../constants/compactDisplay";
import defaultFictionNarrativeLabels from "../../constants/defaultFictionNarrativeLabels";
import itemTypeCategories from "../../constants/itemTypeCategories";
import itemTypeIds from "../../constants/itemTypeIds";
import narrativeNumberingOptions from "../../constants/narrativeNumberingOptions";
import { REFERENCE_RELATES_TO } from "../../constants/references";
import subwayDisplayOptions from "../../constants/subwayDisplayOptions";
import { TimelineZoomLimits } from "../../constants/zoomRange";
import { getGregorianCalendarObject } from "../../modules/definition/calendar/gregorianCalendarRaw";
import arrayToListObjectCamelCase from "../../utils/arrayToListObjectCamelCase";

const calendar = getGregorianCalendarObject();

export default {
  autoAdjustLevel: AutoAdjustDateLevel.ResolveConstraints,
  autoAdjustBackwardsLimit: AutoAdjustBackwardsLimit.Off,
  includeNarrative: true,
  types: {
    byId: {
      species: {
        enabled: true,
        canDelete: true,
        id: "species",
        label: "Species",
        pluralLabel: "Species",
        labelDisplayLabel: "Name",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO, "homeworldRef"],
        properties: [],
        superTypes: ["species"],
        icon: "flag",
        color: "iconYellow",
        defaultItemColor: null,
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        identifierPrefix: "SPE",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      technology: {
        enabled: true,
        canDelete: true,
        id: "technology",
        label: "Technology",
        pluralLabel: "Technologies",
        labelDisplayLabel: "Name",
        startDisplayLabel: "Invented",
        endDisplayLabel: "Retired",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO, "speciesRef"],
        properties: [],
        superTypes: ["technology"],
        icon: "organisation",
        color: "iconGreen",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        identifierPrefix: "TEC",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      spacecraft: {
        enabled: true,
        canDelete: true,
        id: "spacecraft",
        label: "Spacecraft",
        pluralLabel: "Spacecraft",
        labelDisplayLabel: "Name",
        startDisplayLabel: "Commissioned",
        endDisplayLabel: "Decommissioned",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO, "speciesRef"],
        properties: [],
        superTypes: ["spacecraft"],
        icon: "cursor",
        color: "iconPurple",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        identifierPrefix: "SC",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      theme: {
        enabled: true,
        canDelete: true,
        id: "theme",
        label: "Theme",
        pluralLabel: "Themes",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO],
        properties: ["notes"],
        superTypes: ["theme"],
        icon: "droplet",
        color: "iconPurple",
        defaultItemColor: null,
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        identifierPrefix: "TH",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      backstory: {
        enabled: true,
        canDelete: true,
        id: "backstory",
        label: "Backstory",
        pluralLabel: "Backstories",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          "witnessRef",
          "locationRef",
          "participantRef",
          "storyArcRef",
          "themeRef",
          "causesRef",
          "seeAlsoRef",
          REFERENCE_RELATES_TO,
          "startLocationRef",
          "endLocationRef",
          "spacecraftRef",
          "technologyRef"
        ],
        properties: ["notes", "tension"],
        superTypes: [itemTypeIds.EVENT_ITEM_TYPE_ID, "flashback", "flashForward", "backstory"],
        icon: "scroll",
        color: "iconPurple",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "BAK",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      [itemTypeIds.NARRATIVE_FOLDER_TYPE_ID]: {
        enabled: true,
        canDelete: false,
        id: itemTypeIds.NARRATIVE_FOLDER_TYPE_ID,
        label: "Narrative Folder",
        pluralLabel: "Narrative Folders",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          "witnessRef",
          "participantRef",
          "storyArcRef",
          "locationRef",
          "themeRef",
          REFERENCE_RELATES_TO,
          "startLocationRef",
          "endLocationRef"
        ],
        properties: ["notes", "tension"],
        superTypes: [],
        icon: "folder",
        color: "iconMono",
        defaultItemColor: null,
        canHaveDate: false,
        canIncludeInNarrative: true,
        showInSequence: false,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "NF",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: true,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      [itemTypeIds.EXPOSITION_ITEM_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.EXPOSITION_ITEM_TYPE_ID,
        label: "Exposition",
        pluralLabel: "Expositions",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          "witnessRef",
          "participantRef",
          "storyArcRef",
          "locationRef",
          "themeRef",
          "causesRef",
          "seeAlsoRef",
          REFERENCE_RELATES_TO,
          "startLocationRef",
          "endLocationRef",
          "spacecraftRef",
          "technologyRef"
        ],
        properties: ["notes"],
        superTypes: [
          itemTypeIds.EXPOSITION_ITEM_TYPE_ID,
          itemTypeIds.EVENT_ITEM_TYPE_ID,
          "flashback",
          "flashForward",
          "backstory",
          itemTypeIds.NARRATIVE_FOLDER_TYPE_ID
        ],
        icon: "scroll",
        color: "iconMono",
        canHaveDate: false,
        canIncludeInNarrative: true,
        showInSequence: false,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "EX",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isRelative: false,
        relativeUnits: "seconds",
        defaultSequenceType: false,
        defaultNarrativeType: false,
        isNarrativeFolder: false
      },
      flashForward: {
        enabled: true,
        canDelete: true,
        id: "flashForward",
        label: "Flash-Forward",
        pluralLabel: "Flash-Forwards",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          "witnessRef",
          "locationRef",
          "participantRef",
          "storyArcRef",
          "themeRef",
          "causesRef",
          "seeAlsoRef",
          REFERENCE_RELATES_TO,
          "startLocationRef",
          "endLocationRef",
          "spacecraftRef",
          "technologyRef"
        ],
        properties: ["notes", "tension"],
        superTypes: [itemTypeIds.EVENT_ITEM_TYPE_ID, "flashForward", "flashback", "backstory"],
        icon: "fastForward",
        color: "iconRed",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: true,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "FF",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      characterGroup: {
        enabled: true,
        canDelete: true,
        id: "characterGroup",
        label: "Character Group",
        pluralLabel: "Character Groups",
        labelDisplayLabel: "Name",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: false,
        references: [REFERENCE_RELATES_TO],
        properties: ["notes"],
        superTypes: ["characterGroup"],
        icon: "people",
        color: "iconGreen",
        defaultItemColor: null,
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        identifierPrefix: "CG",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      [itemTypeIds.PERSON_ITEM_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.PERSON_ITEM_TYPE_ID,
        label: "Character",
        pluralLabel: "Characters",
        labelDisplayLabel: "Name",
        startDisplayLabel: "Birth",
        endDisplayLabel: "Death",
        anniversaryDisplayLabel: "Birthday",
        extendAnniversaryBeyondEnd: false,
        references: [
          "instigatorRef",
          "themeRef",
          "birthplaceRef",
          "familyRef",
          "friendRef",
          "associateRef",
          REFERENCE_RELATES_TO,
          "homeRef",
          "parentRef",
          "rivalRef",
          "protagonistRef",
          "antagonistRef",
          "grandparentRef",
          "stepParentRef",
          "siblingRef",
          "spouseRef",
          "partnerRef",
          "neighbourRef",
          "fianceRef",
          "speciesRef"
        ],
        properties: [
          "notes",
          "complete",
          "motivation",
          "nickname",
          "characterType",
          "occupation",
          "gender",
          "characteristics",
          "traits"
        ],
        superTypes: ["characterGroup"],
        icon: "person",
        color: "iconRed",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        columnIsVisible: true,
        identifierPrefix: "PER",
        compactDisplay: compactDisplays.AVATAR_INITIALS,
        category: itemTypeCategories.PERSON,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      [itemTypeIds.LOCATION_ITEM_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.LOCATION_ITEM_TYPE_ID,
        label: "Location",
        pluralLabel: "Locations",
        labelDisplayLabel: "Name",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: ["themeRef", REFERENCE_RELATES_TO],
        properties: ["notes"],
        superTypes: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "map",
        color: "iconOrange",
        defaultItemColor: null,
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        columnIsVisible: true,
        identifierPrefix: "LOC",
        compactDisplay: compactDisplays.SHORT_LABEL,
        category: itemTypeCategories.LOCATION,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      flashback: {
        enabled: true,
        canDelete: true,
        id: "flashback",
        label: "Flashback",
        pluralLabel: "Flashbacks",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          "witnessRef",
          "locationRef",
          "participantRef",
          "storyArcRef",
          "themeRef",
          "causesRef",
          "seeAlsoRef",
          REFERENCE_RELATES_TO,
          "startLocationRef",
          "endLocationRef",
          "spacecraftRef",
          "technologyRef"
        ],
        properties: ["notes", "tension"],
        superTypes: [itemTypeIds.EVENT_ITEM_TYPE_ID, "flashback", "flashForward", "backstory"],
        icon: "rewind",
        color: "iconGreen",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: true,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "FL",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      [itemTypeIds.MINDMAP_NODE_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.MINDMAP_NODE_TYPE_ID,
        label: "Mindmap Node",
        pluralLabel: "Nodes",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO],
        properties: ["notes"],
        superTypes: [],
        icon: "diamond",
        color: "iconMono",
        defaultItemColor: null,
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "NO",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      [itemTypeIds.EVENT_ITEM_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.EVENT_ITEM_TYPE_ID,
        label: "Event",
        pluralLabel: "Events",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          "witnessRef",
          "participantRef",
          "storyArcRef",
          "locationRef",
          "themeRef",
          "causesRef",
          "seeAlsoRef",
          REFERENCE_RELATES_TO,
          "startLocationRef",
          "endLocationRef",
          "spacecraftRef",
          "technologyRef"
        ],
        properties: ["notes", "tension"],
        superTypes: [itemTypeIds.EVENT_ITEM_TYPE_ID, "flashback", "flashForward", "backstory"],
        icon: "diamond",
        color: "iconYellow",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: true,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "EV",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: true,
        defaultNarrativeType: true,
        canHaveLocationUri: false
      },
      storyArc: {
        enabled: true,
        canDelete: true,
        id: "storyArc",
        label: "Story Arc",
        pluralLabel: "Story Arcs",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: ["themeRef", REFERENCE_RELATES_TO, "setupRef", "climaxRef", "resolutionRef"],
        properties: ["notes", "tension"],
        superTypes: ["storyArc"],
        icon: "star",
        color: "iconYellow",
        defaultItemColor: null,
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        identifierPrefix: "ARC",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      }
    },
    allIds: [
      itemTypeIds.EVENT_ITEM_TYPE_ID,
      "flashback",
      "flashForward",
      "backstory",
      itemTypeIds.PERSON_ITEM_TYPE_ID,
      "characterGroup",
      itemTypeIds.LOCATION_ITEM_TYPE_ID,
      "species",
      "technology",
      "spacecraft",
      "storyArc",
      "theme",
      itemTypeIds.NARRATIVE_FOLDER_TYPE_ID,
      itemTypeIds.EXPOSITION_ITEM_TYPE_ID,
      itemTypeIds.MINDMAP_NODE_TYPE_ID
    ]
  },
  properties: {
    byId: {
      notes: {
        enabled: true,
        canDelete: true,
        id: "notes",
        key: "notes",
        label: "Notes",
        format: "multitext",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      gender: {
        enabled: true,
        canDelete: true,
        id: "gender",
        key: "gender",
        label: "Gender",
        format: "list",
        allowed: arrayToListObjectCamelCase(["Male", "Female", "Other"]),
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      characterType: {
        enabled: true,
        canDelete: true,
        id: "characterType",
        key: "characterType",
        label: "Character Type",
        format: "list",
        allowed: arrayToListObjectCamelCase([
          "Protagonist",
          "Antagonist",
          "Ally",
          "Opponent",
          "Fake-Ally Opponent",
          "Fake-Opponent Ally"
        ]),
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      traits: {
        enabled: true,
        canDelete: true,
        id: "traits",
        key: "traits",
        label: "Traits",
        format: "multitext",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      occupation: {
        enabled: true,
        canDelete: true,
        id: "occupation",
        key: "occupation",
        label: "Occupation",
        format: "text",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      nickname: {
        enabled: true,
        canDelete: true,
        id: "nickname",
        key: "nickname",
        label: "Nickname",
        format: "text",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      tension: {
        enabled: true,
        canDelete: true,
        id: "tension",
        key: "tension",
        label: "Tension",
        format: "percentage",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      motivation: {
        enabled: true,
        canDelete: true,
        id: "motivation",
        key: "motivation",
        label: "Motivation",
        format: "multitext",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      characteristics: {
        enabled: true,
        canDelete: true,
        id: "characteristics",
        key: "characteristics",
        label: "Characteristics",
        format: "multitext",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      complete: {
        enabled: true,
        canDelete: true,
        id: "complete",
        key: "complete",
        label: "Goals",
        format: "multitext",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      }
    },
    allIds: [
      "notes",
      "tension",
      "characterType",
      "nickname",
      "gender",
      "occupation",
      "complete",
      "motivation",
      "characteristics",
      "traits"
    ]
  },
  references: {
    byId: {
      participantRef: {
        enabled: true,
        canDelete: true,
        id: "participantRef",
        key: "participantRef",
        label: "Participant",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle filled",
        color: "midBlue",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      fianceRef: {
        enabled: true,
        canDelete: true,
        id: "fianceRef",
        key: "fianceRef",
        label: "Fiancé",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "purple700A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      endLocationRef: {
        enabled: true,
        canDelete: true,
        id: "endLocationRef",
        key: "endLocationRef",
        label: "End Location",
        types: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "circle",
        color: "red400A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      speciesRef: {
        enabled: true,
        canDelete: true,
        id: "speciesRef",
        key: null,
        label: "Species",
        types: ["species"],
        icon: "circle",
        color: "lime500",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      resolutionRef: {
        enabled: true,
        canDelete: true,
        id: "resolutionRef",
        key: "resolutionRef",
        label: "Resolution",
        types: [
          "flashForward",
          "backstory",
          "flashback",
          itemTypeIds.EVENT_ITEM_TYPE_ID,
          itemTypeIds.NARRATIVE_FOLDER_TYPE_ID
        ],
        icon: "circle",
        color: "red200",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      homeworldRef: {
        enabled: true,
        canDelete: true,
        id: "homeworldRef",
        key: null,
        label: "Homeworld",
        types: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "circle",
        color: "purple200A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      rivalRef: {
        enabled: true,
        canDelete: true,
        id: "rivalRef",
        key: "rivalRef",
        label: "Rival",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "red900",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      causesRef: {
        enabled: true,
        canDelete: true,
        id: "causesRef",
        key: "causesRef",
        label: "Causes",
        types: [itemTypeIds.EVENT_ITEM_TYPE_ID, "flashback", "flashForward", "backstory"],
        icon: "circle",
        color: "yellow",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Caused by",
        showInverse: true,
        showInInspector: false
      },
      parentRef: {
        enabled: true,
        canDelete: true,
        id: "parentRef",
        key: "parentRef",
        label: "Parent",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "green400A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Child",
        showInverse: true,
        showInInspector: false
      },
      themeRef: {
        enabled: true,
        canDelete: true,
        id: "themeRef",
        key: "themeRef",
        label: "Theme",
        types: ["theme"],
        icon: "circle",
        color: "darkBlue",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      partnerRef: {
        enabled: true,
        canDelete: true,
        id: "partnerRef",
        key: "partnerRef",
        label: "Partner",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "yellow200",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      technologyRef: {
        enabled: true,
        canDelete: true,
        id: "technologyRef",
        key: null,
        label: "Technology",
        types: ["technology"],
        icon: "circle",
        color: "lightGreen500",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      birthplaceRef: {
        enabled: true,
        canDelete: true,
        id: "birthplaceRef",
        key: "birthplaceRef",
        label: "Birthplace",
        types: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "circle",
        color: "apple",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      climaxRef: {
        enabled: true,
        canDelete: true,
        id: "climaxRef",
        key: "climaxRef",
        label: "Climax",
        types: [
          "flashForward",
          itemTypeIds.EVENT_ITEM_TYPE_ID,
          "flashback",
          "backstory",
          itemTypeIds.NARRATIVE_FOLDER_TYPE_ID
        ],
        icon: "circle",
        color: "orange",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      familyRef: {
        enabled: true,
        canDelete: true,
        id: "familyRef",
        key: "familyRef",
        label: "Family",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "lightPink",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      spacecraftRef: {
        enabled: true,
        canDelete: true,
        id: "spacecraftRef",
        key: null,
        label: "Spacecraft",
        types: ["spacecraft"],
        icon: "circle",
        color: "midBlue",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      siblingRef: {
        enabled: true,
        canDelete: true,
        id: "siblingRef",
        key: "siblingRef",
        label: "Sibling",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "yellow300",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      homeRef: {
        enabled: true,
        canDelete: true,
        id: "homeRef",
        key: "homeRef",
        label: "Home",
        types: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "circle",
        color: "lightTeal",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      associateRef: {
        enabled: true,
        canDelete: true,
        id: "associateRef",
        key: "associateRef",
        label: "Associate",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "grey",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      friendRef: {
        enabled: true,
        canDelete: true,
        id: "friendRef",
        key: "friendRef",
        label: "Friend",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "fuschia",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      antagonistRef: {
        enabled: true,
        canDelete: true,
        id: "antagonistRef",
        key: "antagonistRef",
        label: "Opponent",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "red",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      protagonistRef: {
        enabled: true,
        canDelete: true,
        id: "protagonistRef",
        key: "protagonistRef",
        label: "Ally",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "lightTeal",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      setupRef: {
        enabled: true,
        canDelete: true,
        id: "setupRef",
        key: "setupRef",
        label: "Setup",
        types: [
          "flashForward",
          itemTypeIds.EVENT_ITEM_TYPE_ID,
          "flashback",
          "backstory",
          itemTypeIds.NARRATIVE_FOLDER_TYPE_ID
        ],
        icon: "circle",
        color: "peach",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      storyArcRef: {
        enabled: true,
        canDelete: true,
        id: "storyArcRef",
        key: "storyArcRef",
        label: "Story Arc",
        types: ["storyArc"],
        icon: "circle filled",
        color: "yellow",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      neighbourRef: {
        enabled: true,
        canDelete: true,
        id: "neighbourRef",
        key: "neighbourRef",
        label: "Neighbour",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "grey300",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      instigatorRef: {
        enabled: true,
        canDelete: true,
        id: "instigatorRef",
        key: "instigatorRef",
        label: "Instigates",
        types: [itemTypeIds.EVENT_ITEM_TYPE_ID, "flashback", "flashForward", "backstory"],
        icon: "circle",
        color: "yellow",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Instigated by",
        showInverse: true,
        showInInspector: false
      },
      seeAlsoRef: {
        enabled: true,
        canDelete: true,
        id: "seeAlsoRef",
        key: "seeAlsoRef",
        label: "See also",
        types: [itemTypeIds.EVENT_ITEM_TYPE_ID, "flashback", "flashForward", "backstory"],
        icon: "circle",
        color: "mono",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      witnessRef: {
        enabled: true,
        canDelete: true,
        id: "witnessRef",
        key: "witnessRef",
        label: "Observer",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "lightBlue",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      startLocationRef: {
        enabled: true,
        canDelete: true,
        id: "startLocationRef",
        key: "startLocationRef",
        label: "Start Location",
        types: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "circle",
        color: "red200A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      stepParentRef: {
        enabled: true,
        canDelete: true,
        id: "stepParentRef",
        key: "stepParentRef",
        label: "Step-Parent",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "teal300",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Stepchild",
        showInverse: true,
        showInInspector: false
      },
      grandparentRef: {
        enabled: true,
        canDelete: true,
        id: "grandparentRef",
        key: "grandparentRef",
        label: "Grandparent",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "teal400A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Grandchild",
        showInverse: true,
        showInInspector: false
      },
      locationRef: {
        enabled: true,
        canDelete: true,
        id: "locationRef",
        key: "locationRef",
        label: "Location",
        types: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "circle filled",
        color: "orange",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      spouseRef: {
        enabled: true,
        canDelete: true,
        id: "spouseRef",
        key: "spouseRef",
        label: "Spouse",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "purple200A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      [REFERENCE_RELATES_TO]: {
        enabled: true,
        canDelete: false,
        id: REFERENCE_RELATES_TO,
        key: REFERENCE_RELATES_TO,
        label: "Relates to",
        types: [
          itemTypeIds.MINDMAP_NODE_TYPE_ID,
          itemTypeIds.EVENT_ITEM_TYPE_ID,
          "flashForward",
          "flashback",
          "backstory",
          itemTypeIds.PERSON_ITEM_TYPE_ID,
          "characterGroup",
          itemTypeIds.LOCATION_ITEM_TYPE_ID,
          "storyArc",
          itemTypeIds.NARRATIVE_FOLDER_TYPE_ID,
          "theme",
          "species",
          "technology",
          "spacecraft",
          itemTypeIds.EXPOSITION_ITEM_TYPE_ID
        ],
        icon: "circle",
        color: "mono",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      }
    },
    allIds: [
      "witnessRef",
      "participantRef",
      "causesRef",
      "instigatorRef",
      "seeAlsoRef",
      "storyArcRef",
      "setupRef",
      "climaxRef",
      "resolutionRef",
      "locationRef",
      "startLocationRef",
      "endLocationRef",
      "birthplaceRef",
      "homeRef",
      "familyRef",
      "friendRef",
      "rivalRef",
      "associateRef",
      "protagonistRef",
      "antagonistRef",
      "parentRef",
      "grandparentRef",
      "stepParentRef",
      "siblingRef",
      "spouseRef",
      "fianceRef",
      "speciesRef",
      "homeworldRef",
      "spacecraftRef",
      "technologyRef",
      "partnerRef",
      "neighbourRef",
      "themeRef",
      REFERENCE_RELATES_TO
    ]
  },
  calendar,
  colors: {
    byId: {
      "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC": {
        id: "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC",
        label: "Light Purple",
        swatchColor: "lightPurple"
      },
      "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5": {
        id: "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5",
        label: "Light Teal",
        swatchColor: "lightTeal"
      },
      "88F01362-D7BA-4AD9-8C93-E0EC45A604B4": {
        id: "88F01362-D7BA-4AD9-8C93-E0EC45A604B4",
        label: "Denim",
        swatchColor: "denim"
      },
      "D6DB1C49-D913-4D7C-884B-1F7AF1F53187": {
        id: "D6DB1C49-D913-4D7C-884B-1F7AF1F53187",
        label: "Yellow",
        swatchColor: "yellow"
      },
      "199ABF77-578A-4EA8-8C2D-8EA080610A78": {
        id: "199ABF77-578A-4EA8-8C2D-8EA080610A78",
        label: "Dark Purple",
        swatchColor: "darkPurple"
      },
      "D9DAECFF-F3CE-4399-9690-5E045E8BFA49": {
        id: "D9DAECFF-F3CE-4399-9690-5E045E8BFA49",
        label: "Light Blue",
        swatchColor: "lightBlue"
      },
      "0CAFEDAF-A92E-4012-9E43-438B826F5FC7": {
        id: "0CAFEDAF-A92E-4012-9E43-438B826F5FC7",
        label: "Apple",
        swatchColor: "apple"
      },
      "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7": {
        id: "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7",
        label: "Red",
        swatchColor: "red"
      },
      "7F4245A2-AF3B-4633-BEBA-68494486C574": {
        id: "7F4245A2-AF3B-4633-BEBA-68494486C574",
        label: "Peach",
        swatchColor: "peach"
      }
    },
    allIds: [
      "0CAFEDAF-A92E-4012-9E43-438B826F5FC7",
      "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC",
      "199ABF77-578A-4EA8-8C2D-8EA080610A78",
      "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5",
      "7F4245A2-AF3B-4633-BEBA-68494486C574",
      "88F01362-D7BA-4AD9-8C93-E0EC45A604B4",
      "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7",
      "D6DB1C49-D913-4D7C-884B-1F7AF1F53187",
      "D9DAECFF-F3CE-4399-9690-5E045E8BFA49"
    ]
  },
  timelineViewSettings: {
    scrollBounds: "infinite",
    eventLayout: "clearAll",
    showIdentifier: false,
    showDates: true,
    showDuration: false,
    showNarrative: false,
    showRelationshipTypes: [],
    showImage: false,
    showSummary: false,
    showInfoIcons: false,
    showDependencies: true,
    zoomFrom: TimelineZoomLimits.Century,
    zoomTo: TimelineZoomLimits.Day,
    showTypes: [itemTypeIds.EVENT_ITEM_TYPE_ID, "flashback", "flashForward", "backstory"]
  },
  narrativeViewSettings: {
    showIdentifier: false,
    showDates: false,
    showDuration: false,
    showNarrative: true,
    showRelatedAvatars: true,
    showImage: false,
    showSummary: false,
    showInfoIcons: false,
    columnLevel: 0
  },
  narrativeSettings: {
    labels: defaultFictionNarrativeLabels,
    autoDeleteWhenRemoveContainer: true,
    numbering: narrativeNumberingOptions.CONTINUOUS_STYLE
  },
  mindmapViewSettings: {
    showIdentifier: true,
    showDates: false,
    showDuration: false,
    showNarrative: false,
    showRelationshipTypes: [],
    showImage: false,
    showSummary: true,
    showInfoIcons: false
  },
  subwayViewSettings: {
    showDates: false,
    display: subwayDisplayOptions.SUBWAY
  },
  relationshipViewSettings: {
    showIdentifier: false,
    showDates: true,
    showDuration: false,
    showNarrative: false,
    showSummary: false,
    showInfoIcons: false
  }
};
